/**
 * Attaching a portal hostname to the deployment.
 *
 * Wrapped behind an interface for two reasons: local dev and CI must never
 * mutate the real Vercel project (that is what `NoopDomainProvider` is for),
 * and the Vercel REST surface is spread across three API versions that we would
 * otherwise be pinning inside a service.
 *
 * No SDK — every call goes through the injected `fetchImpl` so tests can assert
 * the exact URLs.
 */

import { isProductionDeployment } from "@repo/utils";
import type { PortalDomainStatusValue } from "./types";

/** Where an agency points their CNAME. */
export const VERCEL_CNAME_TARGET = "cname.vercel-dns.com";

const VERCEL_API_BASE = "https://api.vercel.com";

/** Normalised provider view of one hostname. */
export interface DomainStatus {
	/** Provider has confirmed ownership. */
	verified: boolean;
	/** DNS does not (yet) resolve to the provider's edge. */
	misconfigured: boolean;
	/** Records the agency still has to create. */
	verification: Array<{
		type: string;
		domain: string;
		value: string;
		reason?: string;
	}>;
	/** Untouched provider payload, kept for debugging only. */
	raw?: unknown;
}

export interface DomainProvider {
	readonly name: "vercel" | "noop";
	add(hostname: string): Promise<DomainStatus>;
	status(hostname: string): Promise<DomainStatus>;
	remove(hostname: string): Promise<void>;
}

/**
 * Map a provider status onto the stored `PortalDomainStatus`.
 *
 * ACTIVE requires both verification *and* working DNS — a verified-but-
 * misconfigured domain would still serve nothing, and showing it as ACTIVE
 * would let an agency enable a portal their client cannot reach. ERROR is never
 * produced here; it is reserved for a failed provider call.
 */
export function toDomainRecordStatus(s: DomainStatus): PortalDomainStatusValue {
	return s.verified && !s.misconfigured ? "ACTIVE" : "PENDING";
}

interface VercelProjectDomain {
	name?: string;
	verified?: boolean;
	verification?: Array<{
		type?: string;
		domain?: string;
		value?: string;
		reason?: string;
	}>;
}

function toVerification(
	domain: VercelProjectDomain,
): DomainStatus["verification"] {
	return (domain.verification ?? []).map((entry) => ({
		type: entry.type ?? "TXT",
		domain: entry.domain ?? "",
		value: entry.value ?? "",
		...(entry.reason ? { reason: entry.reason } : {}),
	}));
}

export interface VercelDomainProviderOptions {
	token: string;
	projectId: string;
	teamId?: string;
	fetchImpl?: typeof fetch;
}

export class VercelDomainProvider implements DomainProvider {
	readonly name = "vercel" as const;

	private readonly token: string;
	private readonly projectId: string;
	private readonly teamId?: string;
	private readonly fetchImpl: typeof fetch;

	constructor(options: VercelDomainProviderOptions) {
		this.token = options.token;
		this.projectId = options.projectId;
		this.teamId = options.teamId;
		this.fetchImpl = options.fetchImpl ?? fetch;
	}

	private url(path: string): string {
		const base = `${VERCEL_API_BASE}${path}`;
		return this.teamId
			? `${base}?teamId=${encodeURIComponent(this.teamId)}`
			: base;
	}

	private async call(
		path: string,
		init: RequestInit = {},
	): Promise<{ status: number; body: unknown }> {
		const response = await this.fetchImpl(this.url(path), {
			...init,
			headers: {
				Authorization: `Bearer ${this.token}`,
				"Content-Type": "application/json",
				...(init.headers ?? {}),
			},
		});

		let body: unknown = null;
		try {
			body = await response.json();
		} catch {
			// Vercel returns an empty body on some DELETEs.
			body = null;
		}

		return { status: response.status, body };
	}

	/** Vercel's error envelope is `{ error: { code, message } }`. */
	private static errorOf(body: unknown): { code: string; message: string } {
		const error = (
			body as { error?: { code?: unknown; message?: unknown } }
		)?.error;
		return {
			code: typeof error?.code === "string" ? error.code : "",
			message: typeof error?.message === "string" ? error.message : "",
		};
	}

	private static fail(action: string, status: number, body: unknown): Error {
		const { message } = VercelDomainProvider.errorOf(body);
		return new Error(
			`Vercel ${action} failed (HTTP ${status})${message ? `: ${message}` : ""}`,
		);
	}

	/**
	 * Add the hostname to the project, then report its status.
	 *
	 * A 409 "already in use" is treated as success and falls through to
	 * {@link status}: re-attaching after a failed first attempt is the common
	 * case. If the domain is in fact held by a *different* project, the
	 * follow-up status call 404s against ours and surfaces the real error, so
	 * this never silently adopts someone else's domain.
	 */
	async add(hostname: string): Promise<DomainStatus> {
		const { status, body } = await this.call(
			`/v10/projects/${encodeURIComponent(this.projectId)}/domains`,
			{ method: "POST", body: JSON.stringify({ name: hostname }) },
		);

		if (status < 200 || status >= 300) {
			const { code, message } = VercelDomainProvider.errorOf(body);
			const alreadyAttached =
				status === 409 &&
				(code === "domain_already_in_use" ||
					code === "domain_already_exists" ||
					/already exists|already in use/i.test(message));

			if (!alreadyAttached) {
				throw VercelDomainProvider.fail("domain add", status, body);
			}
		}

		return await this.status(hostname);
	}

	async status(hostname: string): Promise<DomainStatus> {
		const domainResponse = await this.call(
			`/v9/projects/${encodeURIComponent(this.projectId)}/domains/${encodeURIComponent(hostname)}`,
		);
		if (domainResponse.status < 200 || domainResponse.status >= 300) {
			throw VercelDomainProvider.fail(
				"domain status",
				domainResponse.status,
				domainResponse.body,
			);
		}

		const configResponse = await this.call(
			`/v6/domains/${encodeURIComponent(hostname)}/config`,
		);
		if (configResponse.status < 200 || configResponse.status >= 300) {
			throw VercelDomainProvider.fail(
				"domain config",
				configResponse.status,
				configResponse.body,
			);
		}

		const domain = (domainResponse.body ?? {}) as VercelProjectDomain;
		const config = (configResponse.body ?? {}) as {
			misconfigured?: boolean;
		};

		return {
			verified: domain.verified === true,
			misconfigured: config.misconfigured === true,
			verification: toVerification(domain),
			raw: { domain: domainResponse.body, config: configResponse.body },
		};
	}

	/** Detach the hostname. A 404 means it is already gone, which is the goal. */
	async remove(hostname: string): Promise<void> {
		const { status, body } = await this.call(
			`/v9/projects/${encodeURIComponent(this.projectId)}/domains/${encodeURIComponent(hostname)}`,
			{ method: "DELETE" },
		);

		if (status === 404) {
			return;
		}
		if (status < 200 || status >= 300) {
			throw VercelDomainProvider.fail("domain remove", status, body);
		}
	}
}

/**
 * Stand-in for every environment that must not touch the real project.
 *
 * Reports domains as immediately live so a local or preview portal is usable
 * end to end; nothing about that state is ever written to production DNS.
 */
export class NoopDomainProvider implements DomainProvider {
	readonly name = "noop" as const;

	async add(_hostname: string): Promise<DomainStatus> {
		return { verified: true, misconfigured: false, verification: [] };
	}

	async status(_hostname: string): Promise<DomainStatus> {
		return { verified: true, misconfigured: false, verification: [] };
	}

	async remove(_hostname: string): Promise<void> {
		return;
	}
}

/**
 * The provider for this environment.
 *
 * Vercel only on a production deployment with credentials present. Preview
 * deployments share the production Vercel project, so letting them call the
 * domains API would have a preview branch add and remove live customer
 * hostnames.
 */
export function getDomainProvider(
	env: Record<string, string | undefined> = process.env,
): DomainProvider {
	const token = env.VERCEL_API_TOKEN;
	const projectId = env.VERCEL_PROJECT_ID;

	if (isProductionDeployment() && token && projectId) {
		return new VercelDomainProvider({
			token,
			projectId,
			teamId: env.VERCEL_TEAM_ID,
		});
	}

	return new NoopDomainProvider();
}
